import type { MaterialitySurveyModelMinData } from '@apps/materiality-tracker/api/materiality-assessment';
import type { Option } from '@g17eco/molecules/select/types';
import type { SingleValue } from 'react-select';
import { AssessmentDropdown } from '../assessment/AssessmentDropdown';
import { BasicAlert } from '@g17eco/molecules/alert';
import config from '../../../../config';
import { OverviewActions } from './OverviewActions';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import AssessmentNotComplete from '@g17eco/images/assessment-not-complete.gif';
import { isAssessmentPaid } from '@apps/materiality-tracker/utils';
import Dashboard from '@g17eco/molecules/dashboard';
import { LockedInsights } from './LockedInsights';
import { useUnlockAssessment } from '@apps/materiality-tracker/hooks/useUnlockAssessment';
import { isAfter, formatDateUTC } from '@utils/date';
import { MATERIALITY_TRACKER_MESSAGES } from '@apps/materiality-tracker/constants';

interface Props {
  selectedSurvey: MaterialitySurveyModelMinData;
  surveys: MaterialitySurveyModelMinData[];
  onClickItem: (option: SingleValue<Option<string>>) => void;
  children: React.ReactNode;
}

interface PlaceholderContentProps {
  isSurveyOutdated: boolean;
  outputsLockedDate: string | undefined;
  isStaffUser: boolean;
}

const PlaceholderContent = ({ isSurveyOutdated, outputsLockedDate, isStaffUser }: PlaceholderContentProps) => {
  if (isSurveyOutdated) {
    return (
      <div>
        <BasicAlert type='primary' className='d-flex align-items-center mx-3'>
          <i className='fas fa-info-circle mr-2 text-lg' />
          Results for assessments created before {formatDateUTC(config.materialityTracker.outdatedAssessmentDate)} are
          not available here, as reporting was handled manually during that time.
        </BasicAlert>
        <OverviewActions isStaff={isStaffUser} readOnly={true} />
      </div>
    );
  }

  if (outputsLockedDate) {
    return (
      <div className='text-center'>
        <h1 className='text-ThemeTextPlaceholder'>{MATERIALITY_TRACKER_MESSAGES.OUTPUTS_LOCKED}</h1>
        <img src={AssessmentNotComplete} alt='assessment outputs locked' />
      </div>
    );
  }

  return (
    <div className='text-center'>
      <h1 className='text-ThemeTextPlaceholder'>Complete assessment to view insights</h1>
      <img src={AssessmentNotComplete} alt='assessment not complete' />
    </div>
  );
};

export const AssessmentInsightsPlaceholder = (props: Props) => {
  const { selectedSurvey, surveys, onClickItem, children } = props;
  const isStaffUser = useAppSelector(isStaff);
  const { handleUnlock } = useUnlockAssessment();

  if (!isAssessmentPaid(selectedSurvey)) {
    return (
      <Dashboard className='assessmentContainer'>
        <div className='d-flex pl-3 mb-3'>
          <AssessmentDropdown selectedSurvey={selectedSurvey} surveys={surveys} onClickItem={onClickItem} />
        </div>
        <LockedInsights onUnlock={() => handleUnlock(selectedSurvey)} />
      </Dashboard>
    );
  }

  const isSurveyOutdated = !isAfter(config.materialityTracker.outdatedAssessmentDate, selectedSurvey.created);
  const canGenerateScore =
    Boolean(selectedSurvey.completedDate) && !isSurveyOutdated && !selectedSurvey.outputsLockedDate;

  if (canGenerateScore) {
    return children;
  }

  return (
    <Dashboard className='assessmentContainer'>
      <div className='d-flex pl-3 mb-3'>
        <AssessmentDropdown selectedSurvey={selectedSurvey} surveys={surveys} onClickItem={onClickItem} />
      </div>
      <PlaceholderContent
        isSurveyOutdated={isSurveyOutdated}
        outputsLockedDate={selectedSurvey.outputsLockedDate}
        isStaffUser={isStaffUser}
      />
    </Dashboard>
  );
};
